import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Card } from '@cards/Card';
import { ProfilePictureEditorCard } from '@components/ProfilePicture';
import { createPepHealthService } from '@api/createPepHealthService';
import { selectCurrentDashboardUser } from '@redux/login/reducer';
import { updateOwnProfilePicture } from '@redux/login/actions';

/**
 * My Profile mount for the reusable Profile Picture editor (self lifecycle).
 * Picture Save/Remove stay independent of Cognito MFA Save.
 * Always loads via self canonical GET; session fields refresh after committed changes.
 */
function OwnProfilePictureCard() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const user = useSelector(selectCurrentDashboardUser);
  const [api, setApi] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    createPepHealthService()
      .then(service => {
        if (!cancelled) setApi(service);
      })
      .catch(err => {
        console.error(err);
        if (!cancelled) setError(err);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const onCanonicalChange = useCallback(
    canonical => {
      dispatch(
        updateOwnProfilePicture({
          profilePictureImageId: canonical.profilePictureImageId,
          profilePictureUrl: canonical.profilePictureUrl,
        }),
      );
    },
    [dispatch],
  );

  if (error) {
    return (
      <Card title={t('common:profilePicture.editor.title')} className="profile-picture-editor-card">
        <div className="profile-picture-editor-error" role="alert">
          {t('common:profilePicture.errors.loadFailed')}
        </div>
      </Card>
    );
  }

  if (!api) {
    return null;
  }

  const profile = user.profile || {};
  const displayName =
    user.name ||
    [profile.firstName, profile.lastName].filter(Boolean).join(' ') ||
    user.email ||
    '';

  return (
    <ProfilePictureEditorCard
      mode="self"
      api={api}
      displayName={displayName}
      actorId={user && user.id}
      initialCanonical={null}
      onCanonicalChange={onCanonicalChange}
    />
  );
}

export default OwnProfilePictureCard;
